import { defineComponent as _defineComponent } from 'vue';
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, withModifiers as _withModifiers, unref as _unref, createElementBlock as _createElementBlock, withDirectives as _withDirectives, withKeys as _withKeys, Fragment as _Fragment } from "vue";
const _hoisted_1 = ["href", "aria-label"];
const _hoisted_2 = { class: "conversation-accordion__header" };
const _hoisted_3 = ["aria-label", "aria-disabled"];
const _hoisted_4 = {
    id: "describe-regenerate",
    class: "sr-only"
};
const _hoisted_5 = ["aria-label", "onKeydown"];
import { ref, watch, nextTick, onMounted, onUnmounted, computed } from "vue";
import { useLocalStorage } from "@vueuse/core";
import { vTooltip } from "@/common/directives/tip-directive";
import { useContext } from "@/store/context";
import { storeToRefs } from "pinia";
// Components
import RecAccordion from "@/common/components/RecAccordion.vue";
import SummaryContent from "../components/SummaryContent.vue";
import PulsatingDot from "@/common/components/PulsatingDot.vue";
import SvgIcon from "@/common/components/SvgIcon.vue";
import AlertMessage from "@/common/components/AlertMessage.vue";
// Setup
export default /*@__PURE__*/ _defineComponent({
    __name: 'SummaryBlock',
    props: {
        response: {},
        excerptable: { type: Boolean },
        wrapInAccordion: { type: Boolean, default: false },
        clipboardHeader: { default: "" },
        pending: { type: Boolean },
        guidelinesCustomized: { type: Boolean },
        userHasResponse: { type: Boolean },
        taskConfigUrl: {}
    },
    emits: ["regenerate"],
    setup(__props, { emit: __emit }) {
        const props = __props;
        const emit = __emit;
        const { isModerator } = storeToRefs(useContext());
        const accordionTab = ref(0);
        const isDismissed = useLocalStorage("summary_guidelines_alert_dismissed", false);
        const showGuidelinesAlert = computed(() => isModerator.value && !props.guidelinesCustomized && !props.userHasResponse && !isDismissed.value);
        const onDismissAlert = () => {
            isDismissed.value = true;
        };
        let timeout = null;
        watch(accordionTab, async () => {
            await nextTick();
            if (timeout)
                clearTimeout(timeout);
            timeout = setTimeout(() => {
                window.Ramius?.highlight?.drawMarkers?.();
            }, 500);
        });
        // Listen for excerpt mode changes from JSP
        const handleExcerptModeToggle = () => {
            if (props.wrapInAccordion) {
                accordionTab.value = 0;
            }
        };
        onMounted(() => {
            window.addEventListener("loadExcerptPanel", handleExcerptModeToggle);
        });
        onUnmounted(() => {
            window.removeEventListener("loadExcerptPanel", handleExcerptModeToggle);
        });
        // Copy summary link functionality
        const isCopying = ref(false);
        const copySummaryLink = async () => {
            try {
                if (isCopying.value)
                    return;
                isCopying.value = true;
                // Get raw content from summary
                const rawContent = props.response.summary?.rawContent || props.response.summary?.content || "";
                // Use clipboard header if provided, otherwise just the content
                const summaryContent = (props.clipboardHeader || "") + rawContent;
                await navigator.clipboard.writeText(summaryContent);
                // Reset button state after 2 seconds
                setTimeout(() => {
                    isCopying.value = false;
                }, 2000);
            }
            catch (err) {
                console.error("Failed to copy summary:", err);
                // Fallback for older browsers
                const textArea = document.createElement("textarea");
                textArea.value = props.response.summary?.rawContent || props.response.summary?.content || "";
                document.body.appendChild(textArea);
                textArea.select();
                document.execCommand("copy");
                document.body.removeChild(textArea);
                setTimeout(() => {
                    isCopying.value = false;
                }, 2000);
            }
        };
        const isRegenerating = ref(false);
        const regenerateSummary = () => {
            if (isRegenerating.value)
                return;
            isRegenerating.value = true;
            emit("regenerate");
            setTimeout(() => {
                isRegenerating.value = false;
            }, 3000);
        };
        const canRegenerateSummary = computed(() => isModerator.value);
        const isSummaryOutdated = computed(() => !props.pending && !!props.response.summaryGuidelinesOutdated);
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock(_Fragment, null, [
                (showGuidelinesAlert.value && _ctx.wrapInAccordion)
                    ? (_openBlock(), _createBlock(AlertMessage, {
                        key: 0,
                        type: "warning",
                        dismissible: true,
                        onClose: onDismissAlert
                    }, {
                        default: _withCtx(() => [
                            _createElementVNode("span", null, [
                                _createTextVNode(_toDisplayString(_ctx.$t("If this summary isn't what you expected, you can adjust the Summary Guidelines to change how AI summarizes conversations.")) + " ", 1),
                                _createElementVNode("a", {
                                    href: `${_ctx.taskConfigUrl}#summary-guidelines`,
                                    target: "_self",
                                    "aria-label": _ctx.$t('Improve Summary Guidelines')
                                }, _toDisplayString(_ctx.$t("Improve Guidelines")), 9, _hoisted_1)
                            ])
                        ]),
                        _: 1
                    }))
                    : _createCommentVNode("", true),
                (_ctx.wrapInAccordion)
                    ? (_openBlock(), _createBlock(RecAccordion, {
                        key: 1,
                        modelValue: accordionTab.value,
                        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => ((accordionTab).value = $event)),
                        class: "conversation-accordion",
                        color: "ai-primary"
                    }, {
                        "tab-0-header": _withCtx(() => [
                            _createElementVNode("div", _hoisted_2, [
                                _createElementVNode("span", null, _toDisplayString(_ctx.$t("Conversation Summary")), 1),
                                (canRegenerateSummary.value)
                                    ? _withDirectives((_openBlock(), _createElementBlock("button", {
                                        key: 0,
                                        type: "button",
                                        class: "conversation-accordion__button",
                                        "aria-label": _ctx.$t('Regenerate Summary'),
                                        "aria-disabled": _ctx.pending,
                                        "aria-describedby": "describe-regenerate",
                                        onClick: _withModifiers(regenerateSummary, ["stop"])
                                    }, [
                                        (isSummaryOutdated.value)
                                            ? (_openBlock(), _createBlock(PulsatingDot, { key: 0 }))
                                            : _createCommentVNode("", true),
                                        _createVNode(SvgIcon, {
                                            src: "/svg/dropdown-icons.svg",
                                            icon: "icon-ai-regen"
                                        }),
                                        _createElementVNode("span", null, _toDisplayString(_ctx.$t("Regenerate")), 1),
                                        _createElementVNode("span", _hoisted_4, _toDisplayString(_ctx.$t("Conversation Summary is outdated")), 1)
                                    ], 8, _hoisted_3)), [
                                        [
                                            _unref(vTooltip),
                                            isSummaryOutdated.value ? _ctx.$t('Conversation Summary is outdated') : undefined,
                                            void 0,
                                            { se: true }
                                        ]
                                    ])
                                    : _createCommentVNode("", true),
                                (_ctx.excerptable && _ctx.response.summary)
                                    ? _withDirectives((_openBlock(), _createElementBlock("button", {
                                        key: 1,
                                        type: "button",
                                        class: "conversation-accordion__button",
                                        "aria-label": _ctx.$t('Copy to clipboard'),
                                        onClick: _withModifiers(copySummaryLink, ["stop"]),
                                        onKeydown: [
                                            _withKeys(_withModifiers(copySummaryLink, ["stop"]), ["enter"]),
                                            _withKeys(_withModifiers(copySummaryLink, ["stop"]), ["space"])
                                        ]
                                    }, [
                                        _createVNode(SvgIcon, { icon: "icon-copy" }),
                                        _createElementVNode("span", null, _toDisplayString(isCopying.value ? _ctx.$t("Copied") : _ctx.$t("Copy")), 1)
                                    ], 40, _hoisted_5)), [
                                        [
                                            _unref(vTooltip),
                                            _ctx.$t('Copy to clipboard'),
                                            void 0,
                                            { sw: true }
                                        ]
                                    ])
                                    : _createCommentVNode("", true)
                            ])
                        ]),
                        "tab-0-content": _withCtx(() => [
                            _createVNode(SummaryContent, {
                                excerptable: _ctx.excerptable,
                                response: _ctx.response
                            }, null, 8, ["excerptable", "response"])
                        ]),
                        _: 1
                    }, 8, ["modelValue"]))
                    : (_openBlock(), _createBlock(SummaryContent, {
                        key: 2,
                        excerptable: _ctx.excerptable,
                        response: _ctx.response,
                        "show-expand-toggle": ""
                    }, null, 8, ["excerptable", "response"]))
            ], 64));
        };
    }
});
